// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — the world map view. Canvas 2D, equirectangular, AUTO-FRAMED to the
// countries in play (start, destination, chain) so a regional route is legible at
// 375px. The start is green, the destination amber, the built chain teal (tail
// brightest), and on the reveal the game's optimal route draws in as a gold line —
// "what everyone missed" (principle #9). Countries are NOT labelled except start
// and dest, so the game stays a test of what you know, not a reading exercise.
//
// rAF drives ANIMATION ONLY (flash, pulse, particles, shake) and stops itself when
// idle — game logic never touches it. Guarded against 0-size measures.

import { ALL, country } from './data';
import { frameFor, type Frame, type LonLat } from './geo';
import { PALETTE } from './palette';

export interface Scene {
  startId: string;
  destId: string | null;
  chain: string[];
  reveal: boolean;
  /** The optimal route (route modes, revealed at end); empty otherwise. */
  optimal: string[];
  reducedMotion: boolean;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
}

export interface MapView {
  resize(): void;
  render(scene: Scene): void;
  flashMisfire(): void;
  destroy(): void;
}

const EMPTY: Scene = { startId: '', destId: null, chain: [], reveal: false, optimal: [], reducedMotion: false };

export function createMapView(canvas: HTMLCanvasElement): MapView {
  const ctx = canvas.getContext('2d')!;
  let dpr = 1;
  let cssW = 0;
  let cssH = 0;
  let scale = 1;
  let ox = 0;
  let oy = 0;
  let frame: Frame = { minLon: -180, minLat: -85, maxLon: 180, maxLat: 85 };
  let frameKey = '';

  const paths = new Map<string, Path2D>();
  let scene: Scene = EMPTY;
  let particles: Particle[] = [];
  let flashId: string | null = null;
  let flashAt = 0;
  let shakeUntil = 0;
  let revealAt = 0;
  let lastReveal = false;
  let raf = 0;
  let prevTs = 0;

  const proj = (lon: number, lat: number): [number, number] => [
    ox + (lon - frame.minLon) * scale,
    oy + (frame.maxLat - lat) * scale,
  ];

  function centroidOf(id: string): LonLat | null {
    return country(id)?.c ?? null;
  }

  function recomputeProjection(): void {
    if (cssW < 2 || cssH < 2) return;
    const spanLon = frame.maxLon - frame.minLon;
    const spanLat = frame.maxLat - frame.minLat;
    scale = Math.min(cssW / spanLon, cssH / spanLat);
    const mapW = spanLon * scale;
    const mapH = spanLat * scale;
    ox = (cssW - mapW) / 2;
    oy = (cssH - mapH) / 2;
    paths.clear();
    for (const c of ALL) {
      const p = new Path2D();
      for (const ring of c.rings) {
        for (let i = 0; i < ring.length; i += 2) {
          const [x, y] = proj(ring[i], ring[i + 1]);
          if (i === 0) p.moveTo(x, y);
          else p.lineTo(x, y);
        }
        p.closePath();
      }
      paths.set(c.id, p);
    }
  }

  function updateFrame(): void {
    const pts: LonLat[] = [];
    for (const id of [scene.startId, ...(scene.destId ? [scene.destId] : []), ...scene.chain]) {
      const c = centroidOf(id);
      if (c) pts.push(c);
    }
    const f = frameFor(pts);
    const key = `${f.minLon.toFixed(1)},${f.minLat.toFixed(1)},${f.maxLon.toFixed(1)},${f.maxLat.toFixed(1)}|${cssW}x${cssH}`;
    if (key !== frameKey) {
      frame = f;
      frameKey = key;
      recomputeProjection();
    }
  }

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || canvas.clientWidth;
    const h = rect.height || canvas.clientHeight;
    if (w < 2 || h < 2) return; // transient 0-size → NaN coords; retry next frame
    dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    cssW = w;
    cssH = h;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    frameKey = ''; // force a projection rebuild at the new size
    updateFrame();
    frameDraw();
  }

  function burst(id: string, col: string): void {
    if (scene.reducedMotion) return;
    const c = centroidOf(id);
    if (!c) return;
    const [px, py] = proj(c[0], c[1]);
    for (let i = 0; i < 20; i++) {
      const a = (i / 20) * Math.PI * 2;
      const sp = 0.5 + Math.random() * 1.4;
      particles.push({ x: px, y: py, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 0, max: 460 + Math.random() * 360, col });
    }
  }

  function needsFrame(): boolean {
    const now = performance.now();
    if (particles.length) return true;
    if (flashId && now - flashAt < 520) return true;
    if (now < shakeUntil) return true;
    if (scene.chain.length && !scene.reducedMotion) return true; // tail pulse
    if (scene.reveal && !scene.reducedMotion && now - revealAt < 2400) return true;
    return false;
  }

  function loop(ts: number): void {
    const dt = prevTs ? ts - prevTs : 16;
    prevTs = ts;
    particles = particles.filter((p) => (p.life += dt) < p.max);
    for (const p of particles) {
      p.x += p.vx * dt * 0.06;
      p.y += p.vy * dt * 0.06;
      p.vy += dt * 0.002;
    }
    draw();
    raf = needsFrame() ? requestAnimationFrame(loop) : 0;
  }

  function frameDraw(): void {
    if (!raf) {
      prevTs = 0;
      raf = requestAnimationFrame(loop);
    } else {
      draw();
    }
  }

  function drawPathLine(ids: string[], col: string, width: number, dash: number[]): void {
    const pts = ids.map(centroidOf).filter((c): c is LonLat => !!c).map((c) => proj(c[0], c[1]));
    if (pts.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.strokeStyle = col;
    ctx.lineWidth = width;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.setLineDash(dash);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  function label(id: string, text: string, col: string): void {
    const c = centroidOf(id);
    if (!c) return;
    const [x, y] = proj(c[0], c[1]);
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const w = ctx.measureText(text).width + 12;
    const ry = y - 20;
    ctx.fillStyle = 'rgba(8,21,33,0.86)';
    roundRect(x - w / 2, ry - 9, w, 18, 5);
    ctx.fill();
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.3;
    roundRect(x - w / 2, ry - 9, w, 18, 5);
    ctx.stroke();
    ctx.fillStyle = PALETTE.text;
    ctx.fillText(text, x, ry);
  }

  function roundRect(x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function marker(id: string, col: string, glyph: string): void {
    const c = centroidOf(id);
    if (!c) return;
    const [x, y] = proj(c[0], c[1]);
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.fillStyle = col;
    ctx.fill();
    ctx.strokeStyle = PALETTE.ink;
    ctx.lineWidth = 1.6;
    ctx.stroke();
    ctx.fillStyle = PALETTE.ink;
    ctx.font = '700 9px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(glyph, x, y + 0.5);
  }

  function draw(): void {
    if (cssW < 2) return;
    const now = performance.now();
    const chainSet = new Set(scene.chain);
    const tailId = scene.chain[scene.chain.length - 1];

    ctx.save();
    if (now < shakeUntil && !scene.reducedMotion) {
      const m = (shakeUntil - now) / 260;
      ctx.translate((Math.random() - 0.5) * 6 * m, (Math.random() - 0.5) * 6 * m);
    }

    ctx.fillStyle = PALETTE.sea;
    ctx.fillRect(-8, -8, cssW + 16, cssH + 16);

    // Fills.
    for (const c of ALL) {
      const path = paths.get(c.id)!;
      let fill: string = PALETTE.land;
      if (c.id === scene.startId) fill = PALETTE.start;
      else if (c.id === scene.destId) fill = PALETTE.dest;
      else if (c.id === tailId) fill = PALETTE.tail;
      else if (chainSet.has(c.id)) fill = PALETTE.chain;
      ctx.fillStyle = fill;
      ctx.fill(path);
    }

    // Borders.
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(0.4, scale * 0.02);
    ctx.strokeStyle = PALETTE.landBorder;
    for (const c of ALL) ctx.stroke(paths.get(c.id)!);

    // The optimal-route reveal (only when the round ended unsolved / to show par).
    if (scene.reveal && scene.optimal.length > 1) {
      drawPathLine(scene.optimal, PALETTE.route, 2.6, [7, 5]);
    }

    // The chain the player built.
    if (scene.chain.length > 1) drawPathLine(scene.chain, PALETTE.tail, 3, []);

    // Tail pulse.
    if (tailId && !scene.reveal) {
      const c = centroidOf(tailId);
      if (c) {
        const [x, y] = proj(c[0], c[1]);
        const pulse = scene.reducedMotion ? 0 : 0.5 + 0.5 * Math.sin(now / 240);
        ctx.beginPath();
        ctx.arc(x, y, 6 + pulse * 4, 0, Math.PI * 2);
        ctx.strokeStyle = PALETTE.tail;
        ctx.globalAlpha = 0.7;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    // Markers + labels for the fixed points.
    if (scene.destId) {
      marker(scene.destId, PALETTE.dest, '⚑');
    }
    marker(scene.startId, PALETTE.start, '▶');
    const startC = country(scene.startId);
    if (startC) label(scene.startId, startC.name, PALETTE.start);
    if (scene.destId) {
      const dc = country(scene.destId);
      if (dc) label(scene.destId, dc.name, PALETTE.dest);
    }

    // Flash the just-added country.
    if (flashId && now - flashAt < 520) {
      const p = paths.get(flashId);
      if (p) {
        ctx.save();
        ctx.globalAlpha = (1 - (now - flashAt) / 520) * 0.9;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke(p);
        ctx.restore();
      }
    }

    // Particles.
    for (const p of particles) {
      ctx.globalAlpha = Math.max(0, 1 - p.life / p.max);
      ctx.fillStyle = p.col;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  function render(next: Scene): void {
    const prevChain = scene.chain;
    scene = next;
    updateFrame();
    if (next.chain.length > prevChain.length) {
      const added = next.chain[next.chain.length - 1];
      flashId = added;
      flashAt = performance.now();
      burst(added, added === next.destId ? PALETTE.dest : PALETTE.tail);
    }
    if (next.reveal && !lastReveal) {
      revealAt = performance.now();
      if (next.optimal.length && !scene.reducedMotion) burst(next.destId ?? next.startId, PALETTE.route);
    }
    lastReveal = next.reveal;
    frameDraw();
  }

  function flashMisfire(): void {
    shakeUntil = performance.now() + 260;
    frameDraw();
  }

  const onWin = (): void => resize();
  window.addEventListener('resize', onWin);
  window.addEventListener('orientationchange', onWin);
  resize();

  return {
    resize,
    render,
    flashMisfire,
    destroy() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      window.removeEventListener('resize', onWin);
      window.removeEventListener('orientationchange', onWin);
    },
  };
}
